import { useNavigate, Link } from "react-router-dom";
import Hero from "@/components/Hero";
import { PricingSection } from "@/components/PricingSection";
import { useAuth } from "@/context/AuthContext";
import { Zap, Cast, Wand2 } from "lucide-react";

const FEATURES = [
    {
        icon: Zap,
        title: "Near-Zero Latency",
        text: "WebRTC pipeline transforms every frame at 30 FPS while you talk, move and stream.",
    },
    {
        icon: Cast,
        title: "OBS Ready",
        text: "Drop your private SPCLIVE link into an OBS Browser Source — clean transformed output, no UI.",
    },
    {
        icon: Wand2,
        title: "Prompt-Driven Styles",
        text: "Describe any look — anime, cyberpunk, oil painting — and swap styles live mid-stream.",
    },
];

const HomeHeader = ({ loggedIn, navigate }) => (
    <header className="border-b border-white/10 bg-obsidian/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link to="/" className="font-display font-bold uppercase tracking-tight text-lg" data-testid="home-logo">
                SPCLIVE<span className="text-blaze">//</span>STUDIO
            </Link>
            <nav className="flex items-center gap-2 sm:gap-4">
                <a
                    href="#pricing"
                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white px-2 py-2 transition-colors"
                    data-testid="nav-pricing-link"
                >
                    Pricing
                </a>
                {loggedIn ? (
                    <button
                        onClick={() => navigate("/console")}
                        className="bg-blaze hover:bg-blazeHover text-white font-mono uppercase text-[11px] tracking-[0.2em] px-5 py-2.5 transition-colors"
                        data-testid="nav-console-button"
                    >
                        Console →
                    </button>
                ) : (
                    <>
                        <Link
                            to="/login"
                            className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white px-2 py-2 transition-colors"
                            data-testid="nav-login-link"
                        >
                            Login
                        </Link>
                        <Link
                            to="/register"
                            className="bg-blaze hover:bg-blazeHover text-white font-mono uppercase text-[11px] tracking-[0.2em] px-5 py-2.5 transition-colors"
                            data-testid="nav-register-button"
                        >
                            Get Started
                        </Link>
                    </>
                )}
            </nav>
        </div>
    </header>
);

const FeaturesSection = () => (
    <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16 border-b border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
                <div key={f.title} className="border border-white/10 bg-surface p-6">
                    <f.icon className="w-5 h-5 text-blaze mb-4" />
                    <div className="font-display font-bold uppercase text-lg mb-2">{f.title}</div>
                    <p className="font-mono text-xs text-white/50 leading-relaxed">{f.text}</p>
                </div>
            ))}
        </div>
    </section>
);

const HomeFooter = () => (
    <footer className="border-t border-white/10 bg-black/40">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                Powered by{" "}
                <a href="https://decart.ai" target="_blank" rel="noreferrer" className="text-blaze hover:underline">
                    Lucy AI · SPCLIVE 2.5
                </a>
            </div>
            <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.3em]">
                <Link to="/terms" className="text-white/40 hover:text-blaze transition-colors" data-testid="footer-terms-link">
                    Terms of Use
                </Link>
                <Link to="/privacy" className="text-white/40 hover:text-blaze transition-colors" data-testid="footer-privacy-link">
                    Privacy
                </Link>
                <span className="text-white/30 hidden sm:inline">Realtime WebRTC · OBS Ready</span>
            </div>
        </div>
    </footer>
);

export default function Home() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const loggedIn = user && typeof user === "object";

    return (
        <div className="min-h-screen bg-obsidian text-white" data-testid="home-page">
            <HomeHeader loggedIn={loggedIn} navigate={navigate} />
            <Hero
                onStart={() => navigate(loggedIn ? "/console/stream" : "/register")}
                ctaLabel={loggedIn ? "Open Your Studio" : "Start Streaming"}
            />
            <FeaturesSection />
            <PricingSection
                onSelect={() => navigate(loggedIn ? "/console" : "/register")}
                ctaLabel={loggedIn ? "Top Up in Console" : "Create Account"}
            />
            <HomeFooter />
        </div>
    );
}
